/**
 * Staff pet-detail "Feeding plan" card (migration 0101).
 *
 * Loads the pet's default plan, renders the read-only summary, and flips to
 * the structured editor on Edit. Save → PUT /api/pets/:id/feeding-plan.
 * Falls back to the legacy `pets.feeding_instructions` text when no plan
 * exists yet (that text stays readable until a plan replaces it).
 */
import { FeedingPlanEditor } from '@/components/FeedingPlanEditor';
import { FeedingPlanSummary } from '@/components/FeedingPlanSummary';
import {
  type FeedingPlanDraft,
  type FeedingPlanView,
  draftFromPlan,
  getPetFeedingPlan,
  putPetFeedingPlan,
  validateFeedingDraft,
} from '@/lib/feeding';
import { Loader2, Pencil, Utensils } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

interface PetFeedingPlanPanelProps {
  petId: string;
  /** Legacy free text from the pet row, shown when there is no plan. */
  legacyText?: string | null;
  /** Hide the Edit affordance (e.g. viewer lacks pets.edit). */
  readonly?: boolean;
}

export function PetFeedingPlanPanel({
  petId,
  legacyText: legacyProp,
  readonly = false,
}: PetFeedingPlanPanelProps) {
  const [plan, setPlan] = useState<FeedingPlanView | null>(null);
  const [legacyText, setLegacyText] = useState<string | null>(legacyProp ?? null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<FeedingPlanDraft>(draftFromPlan(null));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getPetFeedingPlan(petId);
      setPlan(res.plan);
      setLegacyText(res.legacyText ?? legacyProp ?? null);
    } catch (e) {
      setError((e as Error).message || 'Could not load feeding plan');
    } finally {
      setLoading(false);
    }
  }, [petId, legacyProp]);

  useEffect(() => {
    void load();
  }, [load]);

  function startEdit() {
    setDraft(draftFromPlan(plan));
    setEditing(true);
    setError(null);
  }

  async function save() {
    const issues = validateFeedingDraft(draft);
    if (issues.length > 0) {
      setError(issues.join('. '));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await putPetFeedingPlan(petId, draft);
      setPlan(res.plan);
      setEditing(false);
    } catch (e) {
      setError((e as Error).message || 'Could not save feeding plan');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="elevate rounded-xl bg-surface ring-1 ring-border"
      data-testid="pet-feeding-plan"
    >
      <div className="flex items-center gap-2 border-b border-border px-5 py-3.5">
        <Utensils className="size-4 text-muted-foreground" strokeWidth={1.75} />
        <h3 className="text-sm font-semibold text-foreground">Feeding plan</h3>
        {!readonly && !editing && !loading && (
          <button
            type="button"
            onClick={startEdit}
            className="press ml-auto inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs font-medium hover:bg-secondary"
          >
            <Pencil className="size-3" strokeWidth={2} />
            {plan ? 'Edit' : 'Add plan'}
          </button>
        )}
      </div>
      <div className="px-5 py-3">
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : editing ? (
          <div className="space-y-3">
            <FeedingPlanEditor value={draft} onChange={setDraft} idPrefix={`pet-${petId}`} />
            {error && <p className="text-xs text-destructive">{error}</p>}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => void save()}
                disabled={saving}
                className="press inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90 disabled:opacity-60"
              >
                {saving && <Loader2 className="size-3.5 animate-spin" strokeWidth={1.75} />}
                Save plan
              </button>
              <button
                type="button"
                onClick={() => setEditing(false)}
                disabled={saving}
                className="press rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            <FeedingPlanSummary
              items={plan?.items ?? []}
              ownerSupplied={plan?.ownerSupplied ?? false}
              allergies={plan?.allergies ?? null}
              redFlags={plan?.redFlags ?? null}
              legacyText={plan ? null : legacyText}
              emptyLabel="No feeding plan yet — add one so run cards and report cards can show each meal."
            />
            {!plan && legacyText && (
              <p className="mt-2 text-[11px] text-muted-foreground">
                Free-text instructions shown. Add a structured plan to get tick boxes on the run
                card.
              </p>
            )}
            {error && <p className="mt-2 text-xs text-destructive">{error}</p>}
          </>
        )}
      </div>
    </div>
  );
}
